/**
 * /admin/audit-log — paginated audit-event viewer.
 *
 * Designed for non-technical readers: each row tells the story in a
 * plain-English sentence (via auditDescriptions.ts) instead of raw
 * action keys and JSON. The structured payload is still accessible
 * per-row via a "Show raw" toggle for forensic investigation.
 *
 * Filters:
 *  - Activity type (friendly labels, mapped to the raw action key)
 *  - User (set by clicking "Filter by user" on a row; cleared via
 *    a removable chip on the toolbar — no UUID text entry)
 *
 * Sorted newest first; uses cursor pagination because the table can
 * grow large.
 */

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { type ComponentType, useEffect, useMemo, useState } from 'react';

import {
  fetchAuditLog,
  fetchUsers,
  type AuditCategory,
  type AuditLogEntry,
  type AuditLogFilters,
  type UserListItem,
} from '../../api/admin';
import { describeAuditEvent, relativeTime, type Tone } from './auditDescriptions';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const PAGE_SIZE = 50;

// ─── Filter taxonomy ──────────────────────────────────────────────
// Visible quick-filter chips. Each "category" maps server-side to a
// set of underlying action keys (see admin_audit.CATEGORY_ACTIONS).
// The "all" chip clears the category filter.

interface CategoryChip {
  value: AuditCategory | 'all';
  label: string;
  Icon: ComponentType<{ sx?: object }>;
  description: string;
}

const CATEGORY_CHIPS: CategoryChip[] = [
  {
    value: 'all',
    label: 'All activity',
    Icon: ViewListOutlinedIcon,
    description: 'Every recorded event across users, accounts, and admin actions.',
  },
  {
    value: 'signins',
    label: 'Sign-ins',
    Icon: LoginOutlinedIcon,
    description: 'Successful and failed sign-in attempts.',
  },
  {
    value: 'signups',
    label: 'Sign-ups',
    Icon: PersonAddAlt1OutlinedIcon,
    description:
      'New sign-up requests, admin approvals or rejections, and accounts created by an administrator.',
  },
  {
    value: 'passwords',
    label: 'Passwords',
    Icon: KeyOutlinedIcon,
    description:
      'Voluntary password changes, forgot-password requests, initial password set, and admin-issued resets.',
  },
  {
    value: 'roles',
    label: 'Roles',
    Icon: BadgeOutlinedIcon,
    description: 'Role assignments changed on users, and new roles added to the catalog.',
  },
  {
    value: 'admin_actions',
    label: 'Admin actions',
    Icon: AdminPanelSettingsOutlinedIcon,
    description:
      "Anything an administrator did on someone else's account — create, approve, reject, reset, disable, re-enable.",
  },
  {
    value: 'data_uploads',
    label: 'Data uploads',
    Icon: CloudUploadOutlinedIcon,
    description:
      'Depletion files uploaded from Admin → Data uploads — checked, rejected, confirmed, discarded and loaded — and changes to who gets the load email.',
  },
  {
    value: 'security',
    label: 'Security concerns',
    Icon: SecurityOutlinedIcon,
    description:
      'Failure events worth reviewing — failed sign-ins, invalid reset links, and rejected password changes.',
  },
];

type DateRange = 'all' | 'today' | '7d' | '30d';

interface RangeChip {
  value: DateRange;
  label: string;
}

const RANGE_CHIPS: RangeChip[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

/** Compute the ISO `since` cutoff for a given range chip. */
function rangeToSince(range: DateRange): string | undefined {
  if (range === 'all') return undefined;
  const now = new Date();
  if (range === 'today') {
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.toISOString();
  }
  const days = range === '7d' ? 7 : 30;
  const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return cutoff.toISOString();
}

const TONE_COLORS: Record<Tone, { bg: string; fg: string; dot: string }> = {
  success: { bg: 'rgba(34, 134, 90, 0.10)', fg: '#22865a', dot: '#22865a' },
  error: { bg: 'rgba(178, 47, 47, 0.10)', fg: '#b22f2f', dot: '#b22f2f' },
  info: { bg: 'rgba(50, 110, 184, 0.10)', fg: '#326eb8', dot: '#326eb8' },
  warning: { bg: 'rgba(187, 140, 63, 0.14)', fg: '#a07530', dot: '#bb8c3f' },
  muted: { bg: 'rgba(15, 10, 7, 0.06)', fg: 'rgba(15, 10, 7, 0.5)', dot: 'rgba(15, 10, 7, 0.4)' },
};

export function AuditLogPage() {
  const [category, setCategory] = useState<AuditCategory | 'all'>('all');
  const [range, setRange] = useState<DateRange>('7d');
  // userFilter holds both the id (sent to backend) and the email
  // (shown on the removable chip). Set by clicking "Filter by user"
  // on a row; cleared via the chip's × button.
  const [userFilter, setUserFilter] = useState<{ id: string; email: string | null } | null>(null);
  const [cursors, setCursors] = useState<(number | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const filters = useMemo<AuditLogFilters>(() => {
    const f: AuditLogFilters = { limit: PAGE_SIZE };
    const cursor = cursors[pageIndex];
    if (cursor !== null && cursor !== undefined) f.cursor = cursor;
    if (category !== 'all') f.category = category;
    if (userFilter) f.user_id = userFilter.id;
    const since = rangeToSince(range);
    if (since) f.since = since;
    return f;
  }, [category, range, userFilter, cursors, pageIndex]);

  const query = useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => fetchAuditLog(filters),
  });

  const onNext = () => {
    if (!query.data?.next_cursor) return;
    setCursors([...cursors.slice(0, pageIndex + 1), query.data.next_cursor]);
    setPageIndex(pageIndex + 1);
  };
  const onPrev = () => {
    if (pageIndex === 0) return;
    setPageIndex(pageIndex - 1);
  };
  const resetPaging = () => {
    setCursors([null]);
    setPageIndex(0);
  };

  const items = query.data?.items ?? [];

  return (
    <Stack spacing={2.5}>
      {/* ─── Toolbar ───────────────────────────────────────── */}
      <PremiumCard>
        <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
          {/* Eyebrow strip with a "Filter" cue + summary of what's set */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              pb: 1.25,
              borderBottom: '1px dashed rgba(187, 140, 63, 0.22)',
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'rgba(187, 140, 63, 0.14)',
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TuneOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: GOLD,
              }}
            >
              Filters
            </Typography>
          </Box>

          {/* Three labeled rows — fixed-width label column keeps all
              controls vertically aligned regardless of label length. */}
          <Stack spacing={1.25}>
            <FilterChipGroup
              label="Activity"
              icon={<ViewListOutlinedIcon sx={{ fontSize: 14 }} />}
              value={category}
              onChange={(v) => {
                setCategory(v as AuditCategory | 'all');
                resetPaging();
              }}
              options={CATEGORY_CHIPS.map((c) => ({
                value: c.value,
                label: c.label,
                icon: <c.Icon sx={{ fontSize: 15 }} />,
                description: c.description,
              }))}
            />

            <FilterChipGroup
              label="When"
              icon={<EventNoteOutlinedIcon sx={{ fontSize: 14 }} />}
              value={range}
              onChange={(v) => {
                setRange(v as DateRange);
                resetPaging();
              }}
              options={RANGE_CHIPS.map((r) => ({ value: r.value, label: r.label }))}
            />

            <UserPickerRow
              icon={<PersonSearchOutlinedIcon sx={{ fontSize: 14 }} />}
              value={userFilter}
              onChange={(next) => {
                setUserFilter(next);
                resetPaging();
              }}
            />
          </Stack>
        </Box>
      </PremiumCard>

      {/* ─── Table ─────────────────────────────────────────── */}
      <PremiumCard>
        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        ) : query.error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {(query.error as Error).message}
          </Alert>
        ) : items.length === 0 ? (
          <EmptyState hasFilters={category !== 'all' || !!userFilter || range !== 'all'} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      fontFamily: FONT_INTER,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(15, 10, 7, 0.5)',
                      borderBottom: '1px solid rgba(15, 10, 7, 0.08)',
                      bgcolor: 'rgba(187, 140, 63, 0.04)',
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell sx={{ width: 200 }}>When</TableCell>
                  <TableCell>What happened</TableCell>
                  <TableCell sx={{ width: 240 }}>Who</TableCell>
                  <TableCell sx={{ width: 40 }} aria-hidden />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    onFilterByUser={(id, email) => {
                      setUserFilter({ id, email });
                      resetPaging();
                    }}
                    currentUserFilterId={userFilter?.id ?? null}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(15, 10, 7, 0.08)',
            px: 2,
            py: 1.25,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              color: 'rgba(15, 10, 7, 0.55)',
              letterSpacing: '0.04em',
            }}
          >
            Page {pageIndex + 1}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              disabled={pageIndex === 0}
              onClick={onPrev}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: GOLD,
                '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
                '&.Mui-disabled': { color: 'rgba(15, 10, 7, 0.25)' },
              }}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={!query.data?.next_cursor}
              onClick={onNext}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: GOLD,
                '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
                '&.Mui-disabled': { color: 'rgba(15, 10, 7, 0.25)' },
              }}
            >
              Next
            </Button>
          </Stack>
        </Box>
      </PremiumCard>
    </Stack>
  );
}

interface AuditRowProps {
  entry: AuditLogEntry;
  onFilterByUser: (userId: string, email: string | null) => void;
  currentUserFilterId: string | null;
}

function AuditRow({ entry, onFilterByUser, currentUserFilterId }: AuditRowProps) {
  const [showRaw, setShowRaw] = useState(false);
  const event = describeAuditEvent(entry);
  const tone = TONE_COLORS[event.tone];
  const hasMeta = Object.keys(entry.metadata ?? {}).length > 0;
  const absolute = new Date(entry.occurred_at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const isCurrentFilter = !!entry.user_id && entry.user_id === currentUserFilterId;

  return (
    <>
      <TableRow
        sx={{
          '& td': { borderBottom: 'none', py: 1.5, verticalAlign: 'top' },
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.03)' },
        }}
      >
        {/* WHEN */}
        <TableCell>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12.5,
              fontWeight: 600,
              color: 'rgba(15, 10, 7, 0.7)',
              lineHeight: 1.3,
            }}
          >
            {relativeTime(entry.occurred_at)}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              color: 'rgba(15, 10, 7, 0.45)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {absolute}
          </Typography>
        </TableCell>

        {/* WHAT HAPPENED */}
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: tone.dot,
                mt: 0.85,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: INK,
                  lineHeight: 1.3,
                }}
              >
                {event.title}
              </Typography>
              {event.description && (
                <Typography
                  sx={{
                    mt: 0.25,
                    fontFamily: FONT_INTER,
                    fontSize: 12,
                    color: 'rgba(15, 10, 7, 0.62)',
                    lineHeight: 1.45,
                  }}
                >
                  {event.description}
                </Typography>
              )}
              {hasMeta && (
                <Button
                  size="small"
                  disableRipple
                  onClick={() => setShowRaw((v) => !v)}
                  endIcon={
                    showRaw ? (
                      <ExpandLessIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  sx={{
                    mt: 0.25,
                    ml: -0.75,
                    px: 0.75,
                    py: 0.25,
                    fontFamily: FONT_INTER,
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'rgba(15, 10, 7, 0.4)',
                    '&:hover': { color: GOLD, bgcolor: 'transparent' },
                  }}
                >
                  {showRaw ? 'Hide raw' : 'Show raw'}
                </Button>
              )}
            </Box>
          </Box>
        </TableCell>

        {/* WHO */}
        <TableCell>
          {entry.user_email ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: 'rgba(15, 10, 7, 0.7)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 180,
                }}
              >
                {entry.user_email}
              </Typography>
              {entry.user_id && !isCurrentFilter && (
                <Tooltip title={`Only show events for ${entry.user_email}`} arrow>
                  <IconButton
                    size="small"
                    onClick={() => onFilterByUser(entry.user_id!, entry.user_email)}
                    sx={{
                      color: 'rgba(15, 10, 7, 0.35)',
                      p: 0.4,
                      '&:hover': { color: GOLD, bgcolor: 'rgba(187, 140, 63, 0.08)' },
                    }}
                    aria-label="Filter by this user"
                  >
                    <FilterListIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12,
                color: 'rgba(15, 10, 7, 0.35)',
                fontStyle: 'italic',
              }}
            >
              System / unknown
            </Typography>
          )}
        </TableCell>

        {/* spacer for chevron-column alignment with Users page — kept empty */}
        <TableCell />
      </TableRow>

      {/* Raw payload expansion row */}
      {hasMeta && (
        <TableRow sx={{ '& td': { borderBottom: '1px solid rgba(15, 10, 7, 0.05)', py: 0 } }}>
          <TableCell colSpan={4} sx={{ pt: '0 !important', pb: '0 !important' }}>
            <Collapse in={showRaw} unmountOnExit>
              <Box
                sx={{
                  ml: 26, // align under the "What happened" column dot
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 0.75,
                  bgcolor: 'rgba(15, 10, 7, 0.03)',
                  border: '1px solid rgba(15, 10, 7, 0.06)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'rgba(15, 10, 7, 0.4)',
                    mb: 0.5,
                  }}
                >
                  Event code · {entry.action}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: 'monospace',
                    fontSize: 11,
                    color: 'rgba(15, 10, 7, 0.6)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(entry.metadata, null, 2)}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <Box
      sx={{
        py: 6,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: 'rgba(187, 140, 63, 0.10)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HistoryToggleOffIcon sx={{ fontSize: 28 }} />
      </Box>
      <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 600, color: INK }}>
        No activity to show
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 13,
          color: 'rgba(15, 10, 7, 0.55)',
          maxWidth: 420,
        }}
      >
        {hasFilters
          ? 'No events match the current filters. Try changing or clearing them.'
          : 'No events have been recorded yet.'}
      </Typography>
    </Box>
  );
}

interface FilterChipOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
}

/** Fixed-width labeled-row prefix used by all three filter rows.
 *  Keeps the controls in each row vertically aligned. */
function FilterRowLabel({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        width: { sm: 92 },
        flexShrink: 0,
        color: 'rgba(15, 10, 7, 0.5)',
      }}
    >
      {icon}
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'inherit',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

interface FilterChipGroupProps {
  label: string;
  icon?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: FilterChipOption[];
}

/** Horizontally-scrolling segmented chip group. One chip is always
 *  selected; the selected chip gets the gold-gradient treatment to
 *  pop against the inactive cream chips.
 *
 *  Renders as a fixed-width label column + a flex-wrap chip row, so
 *  rows in the same toolbar stay vertically aligned regardless of
 *  label length. */
function FilterChipGroup({ label, icon, value, onChange, options }: FilterChipGroupProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1.25,
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <FilterRowLabel icon={icon}>{label}</FilterRowLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, flex: 1, minWidth: 0 }}>
        {options.map((opt) => {
          const active = opt.value === value;
          const chip = (
            <Box
              key={opt.value}
              role="button"
              tabIndex={0}
              onClick={() => onChange(opt.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(opt.value);
                }
              }}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.55,
                px: 1.25,
                py: 0.65,
                borderRadius: 1.25,
                fontFamily: FONT_INTER,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.02em',
                transition:
                  'background-color 140ms ease, color 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
                ...(active
                  ? {
                      background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                      color: INK,
                      border: '1px solid transparent',
                      boxShadow: '0 2px 8px rgba(187, 140, 63, 0.20)',
                    }
                  : {
                      bgcolor: 'rgba(255, 250, 235, 0.55)',
                      color: 'rgba(15, 10, 7, 0.68)',
                      border: '1px solid rgba(15, 10, 7, 0.10)',
                      '&:hover': {
                        bgcolor: 'rgba(187, 140, 63, 0.10)',
                        borderColor: 'rgba(187, 140, 63, 0.36)',
                        color: INK,
                      },
                    }),
              }}
            >
              {opt.icon}
              {opt.label}
            </Box>
          );
          if (!opt.description) return chip;
          return (
            <Tooltip
              key={opt.value}
              title={opt.description}
              arrow
              placement="top"
              enterDelay={350}
              enterNextDelay={250}
              slotProps={{
                tooltip: {
                  sx: {
                    fontFamily: FONT_INTER,
                    fontSize: 11.5,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    maxWidth: 280,
                    px: 1.25,
                    py: 0.85,
                  },
                },
              }}
            >
              {chip}
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

interface UserPickerOption {
  id: string;
  email: string | null;
  label: string;
  sub: string;
}

interface UserPickerRowProps {
  icon?: React.ReactNode;
  value: { id: string; email: string | null } | null;
  onChange: (next: { id: string; email: string | null } | null) => void;
}

/**
 * Searchable user-focus picker. Lets an admin proactively narrow the
 * log to events for one person — they don't need to scroll until
 * they spot the user's email and click the row filter icon.
 *
 * Debounced 200ms via a deferred `searchTerm` mirror that fires the
 * users query. Selecting a user sets the parent's userFilter; clearing
 * removes it. The existing per-row "filter by this user" icon still
 * works and stays in sync because both write into the same state.
 */
function UserPickerRow({ icon, value, onChange }: UserPickerRowProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(inputValue.trim()), 200);
    return () => clearTimeout(handle);
  }, [inputValue]);

  // Empty search returns the most recent active users so the dropdown
  // is useful even before anyone has typed.
  const usersQuery = useQuery({
    queryKey: ['admin', 'audit-log', 'user-picker', searchTerm],
    queryFn: () => fetchUsers({ search: searchTerm || undefined, limit: 8 }),
    staleTime: 30_000,
  });

  const options: UserPickerOption[] = useMemo(() => {
    const items: UserListItem[] = usersQuery.data?.items ?? [];
    return items.map((u) => ({
      id: u.id,
      email: u.email,
      label: `${u.first_name} ${u.last_name}`.trim() || u.email,
      sub: u.email,
    }));
  }, [usersQuery.data]);

  // If the parent has a selection that isn't in the current options
  // list (e.g. it came from a row click), surface it as a synthetic
  // option so the Autocomplete can render its selected state.
  const selectedOption: UserPickerOption | null = useMemo(() => {
    if (!value) return null;
    return {
      id: value.id,
      email: value.email,
      label: value.email ?? value.id.slice(0, 8),
      sub: value.email ?? '',
    };
  }, [value]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1.25,
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <FilterRowLabel icon={icon}>User</FilterRowLabel>
      <Autocomplete<UserPickerOption, false, false, false>
        size="small"
        value={selectedOption}
        onChange={(_, next) => {
          if (next) onChange({ id: next.id, email: next.email });
          else onChange(null);
        }}
        inputValue={inputValue}
        onInputChange={(_, next, reason) => {
          if (reason !== 'reset') setInputValue(next);
        }}
        options={options}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={usersQuery.isFetching}
        loadingText="Searching…"
        noOptionsText={searchTerm ? 'No matching users' : 'Type to search'}
        clearOnBlur={false}
        sx={{ flex: 1, minWidth: 240, maxWidth: 520 }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: IVORY_LIGHT,
              backgroundImage: 'none',
              border: '1px solid rgba(187, 140, 63, 0.25)',
              borderRadius: 1.5,
              boxShadow: '0 14px 32px rgba(142, 106, 42, 0.12)',
              mt: 0.5,
              '& .MuiAutocomplete-listbox': {
                py: 0.5,
                '& .MuiAutocomplete-option': {
                  fontFamily: FONT_INTER,
                  fontSize: 13,
                  mx: 0.5,
                  borderRadius: 1,
                  '&[aria-selected="true"]': {
                    bgcolor: 'rgba(187, 140, 63, 0.16)',
                    color: '#8e6a2a',
                  },
                  '&.Mui-focused': {
                    bgcolor: 'rgba(187, 140, 63, 0.10)',
                  },
                },
              },
            },
          },
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 13,
                    fontWeight: 600,
                    color: INK,
                    lineHeight: 1.3,
                  }}
                >
                  {option.label}
                </Typography>
                {option.sub && option.sub !== option.label && (
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      color: 'rgba(15, 10, 7, 0.55)',
                    }}
                  >
                    {option.sub}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selectedOption
                ? 'Showing events for this user'
                : 'Focus on a specific user — name or email'
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 250, 235, 0.55)',
                fontFamily: FONT_INTER,
                fontSize: 14,
                transition: 'background-color 160ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
                '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
                '&.Mui-focused': { bgcolor: '#fffdf6' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />
        )}
      />
    </Box>
  );
}

function PremiumCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: IVORY_LIGHT,
        border: '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 2,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
          zIndex: 1,
        },
      }}
    >
      {children}
    </Box>
  );
}
